import { IMindNode } from "../interfaces/IMindNode";
import { ITreeNodeForRT } from "../interfaces/ITreeNodeForRT";
import { ITreeNodeForSD } from "../interfaces/ITreeNodeForSD";
import { traverseTreeRecursive } from "../../utils/TreeUtils";

/**
 * 对齐工具模块 - 计算树形布局的对齐偏移量
 * 
 * 支持三种对齐模式：
 * 
 * 1. cross-axis-start-edge（交叉轴起始边与视口起始边对齐）
 *    - 将整个树形结构在交叉轴方向的最小坐标对齐到 0
 *    - 水平布局时：将树的最上边对齐到 y=0
 *    - 垂直布局时：将树的最左边对齐到 x=0
 *    - 主轴方向保持相对于原点的位置不变
 * 
 * 2. cross-axis-center（交叉轴居中对齐到视口中心）
 *    - 将整个树形结构在交叉轴方向居中对齐到视口中心
 *    - 水平布局时：树的垂直中心线与视口垂直中心线对齐
 *    - 垂直布局时：树的水平中心线与视口水平中心线对齐
 *    - 主轴方向仍保持相对于原点的位置不变
 *    - 需要提供视口信息
 * 
 * 3. node-center（节点中心居中对齐到视口中心）
 *    - 将指定节点（或根节点）的中心点与视口中心点完全对齐
 *    - 同时在主轴和交叉轴两个方向上都进行居中对齐
 *    - 如果指定了 targetNodeId，则以该节点为对齐目标
 *    - 如果未指定或找不到目标节点，则回退到根节点
 *    - 需要提供视口信息
 * 
 * 注意：前两种模式只在交叉轴方向对齐，第三种模式是完全的双轴居中对齐
 */

export type AlignOptions = {
    // 对齐模式
    mode: 'cross-axis-start-edge' | 'cross-axis-center' | 'node-center',
    // 视口信息，用于 'cross-axis-center' 和 'node-center' 模式
    viewport?: { x: number; y: number; width: number; height: number; },
    // 目标节点 ID，仅用于 'node-center' 模式。如果未提供，则使用根节点。
    targetNodeId?: string | number;
}

/**
 * 计算布局对齐所需的偏移量。
 * @param rootNode 树的根节点 (假设相对坐标已计算)
 * @param align 对齐选项
 * @param isHorizontal 是否为水平布局
 * @returns 对齐偏移量 { x, y }
 */
export function calculateAlignmentOffset(
    rootNode: IMindNode | ITreeNodeForRT | ITreeNodeForSD,
    isHorizontal: boolean,
    align?: AlignOptions,
): { x: number; y: number } {
    const alignmentOffset = { x: 0, y: 0 };

    if (!align) {
        return alignmentOffset; // 如果没有提供对齐选项，则返回零偏移
    }

    // 计算树的边界 (主要用于 cross-axis-start-edge 和 cross-axis-center)
    const bounds = calculateTreeBounds(rootNode);
    const crossAxis = isHorizontal ? 'y' : 'x';
    const mainAxis = isHorizontal ? 'x' : 'y'; // 新增：确定主轴
    const minCrossAxisCoord = isHorizontal ? bounds.minY : bounds.minX;
    const maxCrossAxisCoord = isHorizontal ? bounds.maxY : bounds.maxX;
    const treeCrossAxisSpan = maxCrossAxisCoord - minCrossAxisCoord;

    switch (align.mode) {
        case 'cross-axis-start-edge':
            // 将交叉轴的最小坐标对齐到 0
            alignmentOffset[crossAxis] = -minCrossAxisCoord;
            // 主轴保持不变 (相对 0)
            break;

        case 'cross-axis-center':
            if (align.viewport) {
                const viewportCrossAxisCenter = isHorizontal
                    ? align.viewport.y + align.viewport.height / 2
                    : align.viewport.x + align.viewport.width / 2;
                const treeCrossAxisCenter = minCrossAxisCoord + treeCrossAxisSpan / 2;
                alignmentOffset[crossAxis] = viewportCrossAxisCenter - treeCrossAxisCenter;
                // 主轴保持不变 (相对 0)
            } else {
                console.warn("Alignment mode 'cross-axis-center' requires viewport information.");
            }
            break;

        case 'node-center':
            if (align.viewport) {
                let targetNode: IMindNode | ITreeNodeForRT | ITreeNodeForSD | null = null;
                if (align.targetNodeId !== undefined) {
                    targetNode = findNodeById(rootNode, align.targetNodeId);
                    if (!targetNode) {
                        console.warn(`Target node with ID '${align.targetNodeId}' not found for alignment. Falling back to root node.`);
                        // 如果指定了 ID 但找不到，可以选择回退到根节点或不进行对齐
                        // 这里选择回退到根节点
                        targetNode = rootNode;
                        // 或者，如果你希望找不到节点时完全不进行对齐，可以取消下面的注释并移除 targetNode = rootNode;
                        // console.warn(`Target node with ID '${align.targetNodeId}' not found. No alignment applied.`);
                        // break; // 退出 switch
                    }
                } else {
                    // 如果没有提供 targetNodeId，则使用根节点
                    targetNode = rootNode;
                }

                // 确保有一个有效的 targetNode (无论是找到的、根节点还是回退的)
                if (targetNode) {
                    // --- 交叉轴对齐 ---
                    const viewportCrossAxisCenter = isHorizontal
                        ? align.viewport.y + align.viewport.height / 2
                        : align.viewport.x + align.viewport.width / 2;
                    const nodeCrossAxisCenter = isHorizontal
                        ? targetNode.y + targetNode.height / 2 // 使用相对坐标
                        : targetNode.x + targetNode.width / 2; // 使用相对坐标
                    alignmentOffset[crossAxis] = viewportCrossAxisCenter - nodeCrossAxisCenter;

                    // --- 主轴对齐 ---
                    const viewportMainAxisCenter = isHorizontal
                        ? align.viewport.x + align.viewport.width / 2
                        : align.viewport.y + align.viewport.height / 2;
                    const nodeMainAxisCenter = isHorizontal
                        ? targetNode.x + targetNode.width / 2 // 使用相对坐标
                        : targetNode.y + targetNode.height / 2; // 使用相对坐标
                    alignmentOffset[mainAxis] = viewportMainAxisCenter - nodeMainAxisCenter;
                }
                // 注意：如果 targetNode 最终为 null (例如，如果找不到节点且选择不回退)，则不会执行对齐计算，偏移量保持为 {0, 0}

            } else {
                console.warn("Alignment mode 'node-center' requires viewport information.");
                // 如果缺少视口信息，则不进行任何对齐，保持 (0,0) 偏移
            }
            break;
    }

    return alignmentOffset;
}

/**
 * 计算树布局后的边界框。
 * @param rootNode 树的根节点 (假设 x, y, width, height 已初步计算)
 * @returns 包含 minX, maxX, minY, maxY 的对象
 */
function calculateTreeBounds(rootNode: IMindNode | ITreeNodeForRT | ITreeNodeForSD): { minX: number; maxX: number; minY: number; maxY: number } {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

    traverseTreeRecursive(rootNode, (node) => {
        minX = Math.min(minX, node.x);
        maxX = Math.max(maxX, node.x + node.width);
        minY = Math.min(minY, node.y);
        maxY = Math.max(maxY, node.y + node.height);
    });

    // 如果树为空或只有根节点且未初始化，则返回默认值
    if (minX === Infinity) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    return { minX, maxX, minY, maxY };
}

/**
 * 根据 ID 在树中查找节点。
 * @param node 当前搜索的起始节点
 * @param id 要查找的节点 ID
 * @returns 找到的节点或 null
 */
function findNodeById(
    node: IMindNode | ITreeNodeForRT | ITreeNodeForSD,
    id: string | number
): IMindNode | ITreeNodeForRT | ITreeNodeForSD | null {
    if (node.id === id) {
        return node;
    }
    if (node.children?.length) {
        for (const child of node.children) {
            const found = findNodeById(child, id);
            if (found) {
                return found;
            }
        }
    }
    return null;
}